import { environment } from 'src/environments/environment';

/**
 * Set to `'1'` in `main.ts` immediately before redirecting to ERP `portal-bridge`. If still set on
 * the next portal load (returned without `?at=`), we clear it so SSO can be retried — not a
 * permanent "already tried" lock (that stranded users after logging in on ERP in another tab).
 */
export const MOOTORI_ERP_SSO_ROUNDTRIP_KEY = 'mootori_erp_sso_roundtrip';

/** @deprecated use {@link MOOTORI_ERP_SSO_ROUNDTRIP_KEY} */
export const MOOTORI_PORTAL_SSO_ROUNDTRIP_KEY = MOOTORI_ERP_SSO_ROUNDTRIP_KEY;

/**
 * Set to `'1'` in `extra-bar` `logout()` so the next `main.ts` load skips ERP `portal-bridge` (user
 * explicitly signed out and should land on the portal without an SSO round-trip or “stuck” feel).
 */
export const MOOTORI_PORTAL_SSO_SKIP_ONCE_KEY = 'mootori_portal_sso_skip_once';

/** Same key names as ERP / GMS for a consistent sign-out. */
export const PORTAL_MOOTORI_SESSION_KEYS = [
  'mootori_access_token',
  'mootori_refresh_token',
  'mootori_selected_business_id',
  'business_id',
  'mootori_header_user_label',
  'mootori_header_user_email',
  'mootori_header_businesses_json'
] as const;

export function clearMootoriSharedBrowserKeys(): void {
  for (const k of PORTAL_MOOTORI_SESSION_KEYS) {
    localStorage.removeItem(k);
  }
  try {
    sessionStorage.removeItem('mootori_pending_business_id');
    sessionStorage.removeItem(MOOTORI_ERP_SSO_ROUNDTRIP_KEY);
    sessionStorage.removeItem('mootori_portal_sso_roundtrip');
  } catch {
    /* ignore */
  }
}

/**
 * ERP auth screen with marketing-site handoff. After OTP, ERP redirects back with `?at=` / `?rt=`.
 */
export function buildErpPortalAuthUrl(screen: 'login' | 'register'): string {
  const erp = environment.erpPortalUrl.replace(/\/$/, '');
  const self = environment.portalSelfUrl.replace(/\/$/, '');
  const returnUrl = encodeURIComponent(`${self}/home`);
  return `${erp}/${screen}?handoff=portal&returnUrl=${returnUrl}`;
}

const DEFAULT_GMS_POST_AUTH_PATH = '/dashboard';

/** Path on GMS only — same rules as ERP `safeGmsReturnPath` (leading `/`, no scheme). */
function sanitizeGmsReturnPathForPortal(raw: string | null | undefined): string {
  if (raw == null || raw === '') return DEFAULT_GMS_POST_AUTH_PATH;
  const p = String(raw).trim();
  if (!p.startsWith('/') || p.startsWith('//') || p.includes('://') || p.length > 2048) {
    return DEFAULT_GMS_POST_AUTH_PATH;
  }
  return p;
}

/**
 * ERP login/register from the marketing portal for business (GMS) users. After OTP, ERP redirects
 * to GMS with `?at=` / `?rt=` (same contract as `web-gms` `erpAuthUrlWithGmsHandoff`).
 */
export function buildErpGmsHandoffAuthUrl(
  screen: 'login' | 'register',
  returnPath: string = DEFAULT_GMS_POST_AUTH_PATH
): string {
  const erp = environment.erpPortalUrl.replace(/\/$/, '');
  const safe = sanitizeGmsReturnPathForPortal(returnPath);
  return `${erp}/${screen}?handoff=gms&returnUrl=${encodeURIComponent(safe)}`;
}

/**
 * ERP route that checks `mootori_access_token` on the ERP origin and bounces back with tokens on the portal origin.
 */
export function buildErpPortalBridgeUrl(): string {
  const erp = environment.erpPortalUrl.replace(/\/$/, '');
  const self = typeof window !== 'undefined' ? window.location.href.split('#')[0] : environment.portalSelfUrl;
  return `${erp}/portal-bridge?returnUrl=${encodeURIComponent(self)}`;
}

/**
 * Same redirect chain as ERP/GMS/ACC/Admin `buildCrossAppLogoutStartUrl` — GMS → ACC → Admin → ERP
 * `logout-sync` → ERP `/login`. Used from the portal in a **hidden iframe** so other origins’
 * `localStorage` is cleared without leaving the public page (parent document URL unchanged).
 */
export function buildCrossAppLogoutStartUrl(): string {
  const erpBase = environment.erpPortalUrl.replace(/\/$/, '');
  const erpLogin = `${erpBase}/login`;
  const erpSync = `${erpBase}/logout-sync?next=${encodeURIComponent(erpLogin)}`;
  const gmsBase = environment.gmsUrl?.replace(/\/$/, '');
  const accBase = environment.accUrl?.replace(/\/$/, '');
  const adminBase = environment.adminUrl?.replace(/\/$/, '');
  if (!gmsBase || !accBase || !adminBase) {
    return erpSync;
  }
  const adminSync = `${adminBase}/logout-sync?next=${encodeURIComponent(erpSync)}`;
  const accSync = `${accBase}/logout-sync?next=${encodeURIComponent(adminSync)}`;
  return `${gmsBase}/logout-sync?next=${encodeURIComponent(accSync)}`;
}

let crossAppLogoutIframeCleanup: ReturnType<typeof setTimeout> | null = null;

/**
 * Runs the cross-app logout URL inside a zero-size iframe so GMS/ACC/Admin/ERP session keys are
 * cleared in this browser. The visible portal tab does not navigate; ensure `erpPortalUrl` / sibling
 * URLs match deployment. If an app sends `X-Frame-Options: DENY`, that hop may not run — portal
 * storage is still cleared by the caller.
 */
export function purgeOtherAppsSessionInBackground(): void {
  if (typeof document === 'undefined') return;
  try {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.setAttribute('tabindex', '-1');
    iframe.title = 'Sign out';
    iframe.style.cssText = 'position:absolute;width:0;height:0;border:0;clip:rect(0,0,0,0);overflow:hidden';
    iframe.src = buildCrossAppLogoutStartUrl();
    document.body.appendChild(iframe);
    if (crossAppLogoutIframeCleanup) clearTimeout(crossAppLogoutIframeCleanup);
    crossAppLogoutIframeCleanup = setTimeout(() => {
      crossAppLogoutIframeCleanup = null;
      try {
        iframe.remove();
      } catch {
        /* ignore */
      }
    }, 25000);
  } catch {
    /* ignore */
  }
}
